import type { Channel, Chat as ChatInstance, Message } from '@pubnub/chat';
import { DEFAULT_TIMEZONE } from '@avanta/shared/constants/common';
import type { OptimisticMessage } from '@avanta/shared/types/chat';
import { format as formatTz, toZonedTime } from 'date-fns-tz';
import {
  EMOTIONAL_CHAT_AVAILABLE_END_TIME,
  EMOTIONAL_CHAT_AVAILABLE_START_TIME,
  EMOTIONAL_CHAT_UNAVAILABLE_DAYS,
} from 'constants/chat';

export const ONBOARDING_MESSAGE_META_KEY = 'autoMessage';

export const ONBOARDING_MESSAGE_META_KEYS = {
  DAY_KEY: 'dayKey',
  PATIENT_ID: 'patientId',
  TRIGGER_TIMETOKEN: 'triggerTimetoken',
} as const;

export const ONBOARDING_AUTO_MESSAGE_TYPES = {
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
} as const;

export type OnboardingAutoMessageType =
  (typeof ONBOARDING_AUTO_MESSAGE_TYPES)[keyof typeof ONBOARDING_AUTO_MESSAGE_TYPES];

export const compareTimetokens = (left: string, right: string): number =>
  Number(left) - Number(right);

export const getMessageDate = (timetoken: string): Date | undefined => {
  const milliseconds = Number(timetoken) / 10000;
  if (!Number.isFinite(milliseconds)) return undefined;
  return new Date(milliseconds);
};

export const getCalendarDayKey = (date: Date, timezone = DEFAULT_TIMEZONE): string =>
  formatTz(toZonedTime(date, timezone), 'yyyy-MM-dd', { timeZone: timezone });

const isOnUnavailableDay = (date: Date, timezone: string): boolean => {
  const weekday = Number(formatTz(toZonedTime(date, timezone), 'i', { timeZone: timezone }));
  return EMOTIONAL_CHAT_UNAVAILABLE_DAYS.includes(weekday);
};

const isWithinAvailabilityWindow = (date: Date, timezone: string): boolean => {
  if (!EMOTIONAL_CHAT_AVAILABLE_START_TIME || !EMOTIONAL_CHAT_AVAILABLE_END_TIME) {
    return true;
  }

  const currentTime = formatTz(toZonedTime(date, timezone), 'HH:mm', { timeZone: timezone });
  return (
    currentTime >= EMOTIONAL_CHAT_AVAILABLE_START_TIME &&
    currentTime < EMOTIONAL_CHAT_AVAILABLE_END_TIME
  );
};

export const isEmotionalChatAvailableAt = (date: Date, timezone = DEFAULT_TIMEZONE): boolean => {
  if (isOnUnavailableDay(date, timezone)) return false;
  return isWithinAvailabilityWindow(date, timezone);
};

/**
 * Returns true when the current time in DEFAULT_TIMEZONE falls within the configured
 * availability window.
 */
export const isEmotionalChatAvailable = (): boolean => isEmotionalChatAvailableAt(new Date());

const getAutoMessageType = (message: Message): OnboardingAutoMessageType | null => {
  const value = message.meta?.[ONBOARDING_MESSAGE_META_KEY];
  return value === ONBOARDING_AUTO_MESSAGE_TYPES.AVAILABLE ||
    value === ONBOARDING_AUTO_MESSAGE_TYPES.UNAVAILABLE
    ? value
    : null;
};

const getMetaString = (message: Message, key: string): string | undefined => {
  const value = message.meta?.[key];
  return typeof value === 'string' ? value : undefined;
};

/**
 * Returns true if an automatic onboarding message already exists after the given
 * patient message. This prevents duplicate processing after a remount or reload.
 */
export const hasAutoReplyAfter = (messages: Message[], timetoken: string): boolean =>
  messages.some(
    message =>
      getAutoMessageType(message) !== null && compareTimetokens(message.timetoken, timetoken) > 0
  );

/**
 * Returns true if an available auto-message already exists for this patient and
 * calendar day. The timetoken fallback keeps older messages compatible with the
 * new metadata.
 */
export const hasAvailableAutoMessageForDay = (
  messages: Message[],
  patientId: string,
  dayKey: string
): boolean =>
  messages.some(message => {
    if (getAutoMessageType(message) !== ONBOARDING_AUTO_MESSAGE_TYPES.AVAILABLE) return false;

    const messagePatientId = getMetaString(message, ONBOARDING_MESSAGE_META_KEYS.PATIENT_ID);
    if (messagePatientId && messagePatientId !== patientId) return false;

    const messageDayKey =
      getMetaString(message, ONBOARDING_MESSAGE_META_KEYS.DAY_KEY) ??
      ((): string | undefined => {
        const messageDate = getMessageDate(message.timetoken);
        return messageDate ? getCalendarDayKey(messageDate) : undefined;
      })();

    return messageDayKey === dayKey;
  });

export const getOnboardingMessageType = (
  messages: Message[],
  patientId: string,
  triggerDate: Date
): OnboardingAutoMessageType | null => {
  const dayKey = getCalendarDayKey(triggerDate);

  if (!isEmotionalChatAvailableAt(triggerDate)) {
    return ONBOARDING_AUTO_MESSAGE_TYPES.UNAVAILABLE;
  }

  return hasAvailableAutoMessageForDay(messages, patientId, dayKey)
    ? null
    : ONBOARDING_AUTO_MESSAGE_TYPES.AVAILABLE;
};

/**
 * Returns true if an automatic onboarding message has already been sent for the
 * exact patient trigger. The timetoken fallback supports old metadata.
 */
export const hasAutoReplyForTrigger = (messages: Message[], triggerTimetoken: string): boolean =>
  messages.some(message => {
    if (getAutoMessageType(message) === null) return false;

    const metadataTrigger = getMetaString(message, ONBOARDING_MESSAGE_META_KEYS.TRIGGER_TIMETOKEN);

    return metadataTrigger === undefined
      ? compareTimetokens(message.timetoken, triggerTimetoken) > 0
      : metadataTrigger === triggerTimetoken;
  });

const HISTORY_PAGE_SIZE = 100;
const MAX_HISTORY_PAGES = 100;

export type GetMessagesToProcessResult = {
  messagesToProcess: OptimisticMessage[];
  newestTimetoken: string | null;
};

export const getMessagesToProcess = ({
  confirmedPatientMessages,
  lastSeenTimetoken,
  failedTimetokens,
}: {
  confirmedPatientMessages: OptimisticMessage[];
  lastSeenTimetoken: string | null;
  failedTimetokens: ReadonlySet<string>;
}): GetMessagesToProcessResult => {
  if (confirmedPatientMessages.length === 0) {
    return {
      messagesToProcess: [],
      newestTimetoken: lastSeenTimetoken,
    };
  }

  const newestPatientMessage = confirmedPatientMessages.reduce(
    (newest, message) =>
      compareTimetokens(message.timetoken, newest.timetoken) > 0 ? message : newest,
    confirmedPatientMessages[0]
  );
  const newestTimetoken = newestPatientMessage.timetoken;
  const newPatientMessages =
    lastSeenTimetoken === null
      ? [newestPatientMessage]
      : confirmedPatientMessages
          .filter(message => compareTimetokens(message.timetoken, lastSeenTimetoken) > 0)
          .toSorted((left, right) => compareTimetokens(left.timetoken, right.timetoken));
  const failedPatientMessages = confirmedPatientMessages.filter(message =>
    failedTimetokens.has(message.timetoken)
  );
  const messagesToProcess = [
    ...new Map(
      [...failedPatientMessages, ...newPatientMessages].map(message => [message.timetoken, message])
    ).values(),
  ].toSorted((left, right) => compareTimetokens(left.timetoken, right.timetoken));

  return { messagesToProcess, newestTimetoken };
};

/**
 * Retrieves the complete channel history by following PubNub's paginated history
 * response until there are no more messages or the safety page limit is reached.
 * Boundary messages can be returned in two consecutive pages, so timetokens are
 * used to remove duplicates while preserving the order returned by PubNub.
 */
export const getCompleteHistory = async (channel: Channel): Promise<Message[]> => {
  const history: Message[] = [];
  const seenTimetokens = new Set<string>();
  let endTimetoken: string | undefined;

  for (let page = 0; page < MAX_HISTORY_PAGES; page++) {
    const response = await channel.getHistory({
      count: HISTORY_PAGE_SIZE,
      ...(endTimetoken ? { endTimetoken } : {}),
    });
    const pageMessages = response.messages;

    if (pageMessages.length === 0) break;

    for (const message of pageMessages) {
      const timetoken = message.timetoken;
      if (!seenTimetokens.has(timetoken)) {
        seenTimetokens.add(timetoken);
        history.push(message);
      }
    }

    if (response.isMore === false || pageMessages.length < HISTORY_PAGE_SIZE) break;

    const oldestTimetoken = pageMessages.reduce(
      (oldest, message) =>
        compareTimetokens(message.timetoken, oldest) < 0 ? message.timetoken : oldest,
      pageMessages[0].timetoken
    );

    if (endTimetoken && compareTimetokens(oldestTimetoken, endTimetoken) >= 0) {
      break;
    }

    endTimetoken = oldestTimetoken;
  }

  return history;
};

export type OnboardingState = {
  generation: number;
  conversationKey: string | null;
  lastSeenTimetoken: string | null;
  failedTimetokens: Set<string>;
  queue: Promise<void>;
};

export const resetConversationStateIfNeeded = (
  conversationKey: string,
  state: OnboardingState
): void => {
  if (state.conversationKey === conversationKey) return;

  state.conversationKey = conversationKey;
  state.generation += 1;
  state.lastSeenTimetoken = null;
  state.failedTimetokens.clear();
  state.queue = Promise.resolve();
};

export type ProcessOnboardingMessageParams = {
  patientMessage: OptimisticMessage;
  chat: ChatInstance;
  channelId: string;
  patientId: string;
  availableMessage: string;
  unavailableMessage: string;
  isCurrentGeneration: () => boolean;
};

export type ProcessOnboardingMessageResult = 'handled' | 'skipped';

export const processOnboardingMessage = async ({
  patientMessage,
  chat,
  channelId,
  patientId,
  availableMessage,
  unavailableMessage,
  isCurrentGeneration,
}: ProcessOnboardingMessageParams): Promise<ProcessOnboardingMessageResult> => {
  const triggerTimetoken = patientMessage.timetoken;
  const triggerDate = getMessageDate(triggerTimetoken);

  if (!triggerDate || !isCurrentGeneration()) return 'skipped';

  const channel = await chat.getChannel(channelId);
  if (!channel || !isCurrentGeneration()) return 'skipped';

  const history = await getCompleteHistory(channel);
  if (!isCurrentGeneration()) return 'skipped';

  if (hasAutoReplyForTrigger(history, triggerTimetoken)) return 'handled';

  const dayKey = getCalendarDayKey(triggerDate);
  const messageType: OnboardingAutoMessageType | null = getOnboardingMessageType(
    history,
    patientId,
    triggerDate
  );

  if (!messageType) return 'handled';

  const messageText =
    messageType === ONBOARDING_AUTO_MESSAGE_TYPES.AVAILABLE ? availableMessage : unavailableMessage;
  const meta: Record<string, string> = {
    [ONBOARDING_MESSAGE_META_KEY]: messageType,
    [ONBOARDING_MESSAGE_META_KEYS.DAY_KEY]: dayKey,
    [ONBOARDING_MESSAGE_META_KEYS.PATIENT_ID]: patientId,
    [ONBOARDING_MESSAGE_META_KEYS.TRIGGER_TIMETOKEN]: triggerTimetoken,
  };

  // The psychologist account is also used by human support. Keep this
  // message unread so the agent notices the patient's new message.
  await channel.sendText(messageText, { meta });

  return 'handled';
};

export const enqueueOnboardingMessages = ({
  messagesToProcess,
  state,
  processMessage,
}: {
  messagesToProcess: OptimisticMessage[];
  state: OnboardingState;
  processMessage: (patientMessage: OptimisticMessage) => Promise<ProcessOnboardingMessageResult>;
}): void => {
  for (const patientMessage of messagesToProcess) {
    const triggerTimetoken = patientMessage.timetoken;

    state.queue = state.queue
      .then(async () => {
        const result = await processMessage(patientMessage);
        if (result === 'handled') {
          state.failedTimetokens.delete(triggerTimetoken);
        }
      })
      .catch(error => {
        state.failedTimetokens.add(triggerTimetoken);
        // eslint-disable-next-line no-console
        console.error('Failed to send psychologist onboarding message', error);
      });
  }
};
